// Shared social login button group — Google, Microsoft, Apple, magic link
// Used by both Login and Register flows across web and admin apps
import { useTranslation } from 'react-i18next';
import googleIcon from '../assets/icons/google-icon.svg';
import microsoftIcon from '../assets/icons/microsoft-icon.svg';
import appleIcon from '../assets/icons/apple-icon.svg';
import eventplusIcon from '../assets/icons/eventplus-icon.svg';

interface SocialLoginButtonsProps {
  onGoogleLogin?: () => void;
  onMicrosoftLogin?: () => void;
  onAppleLogin?: () => void;
  onEventPlusLogin?: () => void;
  onMagicLink?: () => void;
  isLoading?: boolean;
}

// Renders divider + social provider buttons + magic link; only renders buttons for provided handlers
export function SocialLoginButtons({
  onGoogleLogin,
  onMicrosoftLogin,
  onAppleLogin,
  onEventPlusLogin,
  onMagicLink,
  isLoading = false,
}: SocialLoginButtonsProps) {
  const { t } = useTranslation('common');

  const hasAny =
    onGoogleLogin || onMicrosoftLogin || onAppleLogin || onEventPlusLogin || onMagicLink;
  if (!hasAny) return null;

  return (
    <>
      <div className="divider text-sm text-base-content/50 my-4">{t('auth.or')}</div>

      <div className="space-y-3">
        {onGoogleLogin && (
          <button
            type="button"
            onClick={onGoogleLogin}
            className="btn btn-outline w-full gap-2 rounded-xl bg-white text-black"
            disabled={isLoading}
          >
            <img src={googleIcon} alt="Google" width={20} height={20} className="shrink-0" />
            {t('auth.continueWithGoogle')}
          </button>
        )}

        {onMicrosoftLogin && (
          <button
            type="button"
            onClick={onMicrosoftLogin}
            className="btn btn-outline w-full gap-2 rounded-xl bg-white text-black"
            disabled={isLoading}
          >
            <img src={microsoftIcon} alt="Microsoft" width={20} height={20} className="shrink-0" />
            {t('auth.continueWithMicrosoft')}
          </button>
        )}

        {onAppleLogin && (
          <button
            type="button"
            onClick={onAppleLogin}
            className="btn btn-outline w-full gap-2 rounded-xl bg-white text-black"
            disabled={isLoading}
          >
            <img src={appleIcon} alt="Apple" width={20} height={20} className="shrink-0" />
            {t('auth.continueWithApple')}
          </button>
        )}

        {onEventPlusLogin && (
          <button
            type="button"
            onClick={onEventPlusLogin}
            className="btn btn-outline w-full gap-2 rounded-xl bg-white text-black"
            disabled={isLoading}
          >
            <img src={eventplusIcon} alt="EventPlus" width={20} height={20} className="shrink-0" />
            {t('auth.continueWithEventPlus')}
          </button>
        )}

        {onMagicLink && (
          <button
            type="button"
            onClick={onMagicLink}
            className="btn btn-outline w-full rounded-xl"
            disabled={isLoading}
          >
            {t('auth.magicLink')}
          </button>
        )}
      </div>
    </>
  );
}
